import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plant, Pause, CheckCircle, ChartLineUp, Phone } from '@phosphor-icons/react';

const getJourneyLabel = (value) => {
  const labels = {
    pronto_atendimento: 'Pronto para Atendimento',
    em_avaliacao: 'Em Avaliação',
    nutricao_futura: 'Nutrição Futura',
    quente: 'Pronto para Atendimento',
    morno: 'Em Avaliação',
    frio: 'Nutrição Futura',
  };
  return labels[value] || value;
};

const getJourneyBadgeClass = (value) => {
  if (value === 'pronto_atendimento' || value === 'quente') return 'bg-emerald-100 text-emerald-700';
  if (value === 'em_avaliacao' || value === 'morno') return 'bg-orange-100 text-orange-700';
  return 'bg-slate-100 text-slate-700';
};


const API = `/api`;

const NurtureDashboard = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSequences();
    const interval = setInterval(fetchSequences, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchSequences = async () => {
    try {
      const { data } = await axios.get(`${API}/nurture`, { withCredentials: true });
      setSequences(data.sequences || []);
    } catch (error) {
      console.error('Error fetching sequences:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const active = sequences.filter((s) => s.status === 'active');
  const paused = sequences.filter((s) => s.status === 'paused');
  const totalTouchpoints = sequences.reduce(
    (sum, s) => sum + (s.touchpoints?.filter((t) => t.status === 'sent').length || 0),
    0
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="nurture-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
            <Plant size={20} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sequências de Nutrição IA</h3>
            <p className="text-xs text-slate-500">Operação automatizada de follow-up - atualiza a cada 30s</p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp size={16} weight="duotone" className="text-emerald-600" />
            <p className="text-xs font-medium text-emerald-900">Ativas</p>
          </div>
          <p className="text-2xl font-bold text-emerald-700">{active.length}</p>
        </div>
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex items-center gap-2 mb-1">
            <Pause size={16} weight="duotone" className="text-orange-600" />
            <p className="text-xs font-medium text-orange-900">Pausadas (auto)</p>
          </div>
          <p className="text-2xl font-bold text-orange-700">{paused.length}</p>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} weight="duotone" className="text-blue-600" />
            <p className="text-xs font-medium text-blue-900">Touchpoints enviados</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{totalTouchpoints}</p>
        </div>
      </div>

      {/* Sequences list */}
      {sequences.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-200">
          <Plant size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Nenhuma sequência ativa no momento</p>
          <p className="text-xs text-slate-400 mt-1">Inicie sequências de nutrição no Kanban</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
          {sequences.map((seq) => {
            const sentCount = seq.touchpoints?.filter((t) => t.status === 'sent').length || 0;
            const totalCount = seq.touchpoints?.length || 5;
            const progress = (sentCount / totalCount) * 100;
            return (
              <div
                key={seq.id}
                className="p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid={`nurture-row-${seq.lead_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm text-slate-900 truncate">{seq.lead_name || seq.lead_id}</p>
                      {seq.lead_classification && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${
                            getJourneyBadgeClass(seq.lead_classification)
                          }`}
                        >
                          {getJourneyLabel(seq.lead_classification)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                      <Phone size={12} /> {seq.lead_phone} · Canal: {seq.channel}
                    </p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium border whitespace-nowrap ${
                      seq.status === 'active'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-orange-50 text-orange-700 border-orange-200'
                    }`}
                  >
                    {seq.status === 'active' ? 'Ativa' : 'Pausada'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-slate-600 font-medium whitespace-nowrap">
                    {sentCount}/{totalCount}
                  </span>
                </div>

                {seq.stop_reason && seq.status === 'paused' && (
                  <p className="text-xs text-orange-600 mt-2">
                    ⏸ {seq.stop_reason === 'lead_responded' ? 'Lead respondeu' : 'Lead virou quente'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NurtureDashboard;